import { useEffect, useState } from "react";

/**
 * `value`, but only after it has held still for `delay` milliseconds.
 *
 * Written for the filter rail's search box. Typing there is free while the
 * filtering happens in memory, and stops being free the moment the term becomes
 * a request: "swimming carnival" is nineteen keystrokes, and nineteen unindexed
 * collection scans is not a type-ahead, it is a denial of service with good
 * intentions.
 *
 * The trailing edge is the one that matters here — the request that should be
 * sent is the one for the term the person finished typing, not the one they
 * started with — so there is deliberately no leading-edge call.
 */
export function useDebouncedValue<T>(value: T, delay: number): T {
	const [settled, setSettled] = useState(value);

	useEffect(() => {
		const id = window.setTimeout(() => setSettled(value), delay);
		/* Cleanup runs on every change, so each keystroke cancels the timer the
		   one before it set and only the last one survives to fire. */
		return () => window.clearTimeout(id);
	}, [value, delay]);

	return settled;
}
